import { GridPaginationModel, GridSortModel } from "@mui/x-data-grid";
import { IFilterModel } from "common/models/GridModels";
import React, { PropsWithChildren, createContext, useState } from "react";

export interface PostModel<FilterModel extends IFilterModel> {
  page?: number;
  pageSize?: number;
  sort?: GridSortModel;
  filter?: FilterModel;
}

type LovelyGridContextData<FilterModel extends IFilterModel> = {
  postModel?: PostModel<FilterModel>;
  filterModel?: FilterModel;
  sortModel?: GridSortModel;
  paginationModel?: GridPaginationModel;
  handleFiltering: (model: FilterModel) => void;
  handlePagination: (model: GridPaginationModel) => void;
  handleSorting: (model: GridSortModel) => void;
  handleClearFilter: () => void;
};

type LovelyGridProviderProps<FilterModel extends IFilterModel> = {
  initFilter: FilterModel;
};

const LovelyGridContext = createContext<LovelyGridContextData<IFilterModel> | undefined>(undefined);

export function LovelyGridProvider<FilterModel extends IFilterModel>({
  initFilter,
  children,
}: PropsWithChildren<LovelyGridProviderProps<FilterModel>>) {
  const [postModel, setPostModel] = useState<PostModel<FilterModel>>(initFilter);

  const handlePagination = (model: GridPaginationModel) => {
    setPostModel((prev) => ({
      ...prev,
      page: model.page,
      pageSize: model.pageSize,
    }));
  };

  const handleFiltering = (model: IFilterModel) => {
    setPostModel((prev) => ({ ...prev, filter: { ...model } }));
  };

  const handleSorting = (model: GridSortModel) => {
    setPostModel((prev) => ({ ...prev, sort: [...model] }));
  };

  const handleClearFilter = () => {
    setPostModel({
      page: 0,
      pageSize: 100,
      filter: { ...initFilter },
      sort: [],
    });
  };

  return (
    <LovelyGridContext.Provider
      value={{
        postModel,
        filterModel: postModel?.filter,
        sortModel: postModel?.sort,
        paginationModel: {
          page: postModel?.page || 0,
          pageSize: postModel?.pageSize || 100,
        },
        handleFiltering,
        handlePagination,
        handleSorting,
        handleClearFilter,
      }}
    >
      {children}
    </LovelyGridContext.Provider>
  );
}

export function useLovelyGridContext<Item extends IFilterModel>() {
  const context = React.useContext<LovelyGridContextData<Item>>(
    LovelyGridContext as unknown as React.Context<LovelyGridContextData<Item>>
  );
  if (!context) {
    throw new Error(
      "useLovelyGridContext must be used under LovelyGridProvider"
    );
  }
  return context;
}
